import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import styles from './MonthPicker.module.css';

const MONTHS_SHORT = [
  'Jan','Feb','Mar','Apr','May','Jun',
  'Jul','Aug','Sep','Oct','Nov','Dec'
];

const today = () => {
  const d = new Date();
  d.setHours(0,0,0,0);
  return d;
};

const toYM = (year, month) => `${year}-${String(month + 1).padStart(2, '0')}`;

const fromYM = (str) => {
  if (!str) return null;
  const [y, m] = str.split('-').map(Number);
  return new Date(y, m - 1, 1);
};

const formatDisplay = (d) => {
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const YearGrid = ({ year, selected, onMonthClick, minDate, maxDate }) => {
  return (
    <div className={styles.monthGrid}>
      <div className={styles.monthsGrid}>
        {MONTHS_SHORT.map((label, month) => {
          const isSelected = selected && selected.getFullYear() === year && selected.getMonth() === month;
          const cellDate   = new Date(year, month, 1);
          const isDisabled =
            (minDate && new Date(year, month + 1, 0) < minDate) ||
            (maxDate && cellDate > maxDate);
          const isCurrent  = year === today().getFullYear() && month === today().getMonth();

          const cls = [
            styles.day,
            isSelected ? styles.daySelected : '',
            isDisabled ? styles.dayDisabled : '',
            isCurrent  ? styles.dayToday    : '',
          ].filter(Boolean).join(' ');

          return (
            <button
              key={label}
              type="button"
              className={cls}
              onClick={() => !isDisabled && onMonthClick(year, month)}
              disabled={isDisabled}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const MonthPicker = ({
  value = '',
  onChange,
  placeholder = 'Select month',
  disabled = false,
  minDate,
  maxDate,
  className = '',
}) => {
  const selectedMonth = value ? fromYM(value) : null;

  const [open, setOpen]         = useState(false);
  const [viewYear, setViewYear] = useState(() => (selectedMonth || today()).getFullYear());

  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (selectedMonth) setViewYear(selectedMonth.getFullYear());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const prevYear = () => setViewYear(y => y - 1);
  const nextYear = () => setViewYear(y => y + 1);

  const handleMonthClick = useCallback((year, month) => {
    onChange(toYM(year, month));
    setOpen(false);
  }, [onChange]);

  const handleClear = (e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange('');
  };

  const minDateObj = minDate ? fromYM(minDate) : null;
  const maxDateObj = maxDate ? fromYM(maxDate) : null;

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${className} ${disabled ? styles.disabled : ''}`}
    >
      <div
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !disabled && setOpen(o => !o); } }}
      >
        <Calendar className={styles.triggerIcon} size={16} />
        <span className={`${styles.triggerText} ${!selectedMonth ? styles.placeholder : ''}`}>
          {formatDisplay(selectedMonth) || placeholder}
        </span>
        {selectedMonth && (
          <button type="button" className={styles.clearButton} onClick={handleClear} title="Clear">
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.calendarArea}>
            <div className={styles.navBar}>
              <button type="button" className={styles.navBtn} onClick={prevYear}>
                <ChevronLeft size={16} />
              </button>
              <span className={styles.yearLabel}>{viewYear}</span>
              <button type="button" className={styles.navBtn} onClick={nextYear}>
                <ChevronRight size={16} />
              </button>
            </div>

            <YearGrid
              year={viewYear}
              selected={selectedMonth}
              onMonthClick={handleMonthClick}
              minDate={minDateObj}
              maxDate={maxDateObj}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthPicker;
